import {
  ANCHOR_PRICE_USD,
  getDynamicPricing,
  getDynamicPricingL2,
  L2_ANCHOR_PRICE_USD,
  L1_MONTHLY_USD,
  L2_MONTHLY_USD,
  L3_MONTHLY_USD,
} from './pricing';
import { CFA_LEVELS } from './levels';
import { LIST_TIER, tierPriceUsd } from './regionalPricing';

/**
 * Checkout SKU ids (client + Stripe metadata.sku).
 * `product` is the Stripe metadata.product value — L1 must stay
 * `cfa-founding-annual-v1` so live founding checkout / _pass.js keep matching.
 */
export const L1_ANNUAL_SKU = 'l1-annual';
export const L2_ANNUAL_SKU = 'l2-annual';
export const L3_ANNUAL_SKU = 'l3-annual';
export const L1_MONTHLY_SKU = 'l1-monthly';
export const L2_MONTHLY_SKU = 'l2-monthly';
export const L3_MONTHLY_SKU = 'l3-monthly';
export const PACKAGE_LIFETIME_SKU = 'package-lifetime';

export const L1_PRODUCT = 'cfa-founding-annual-v1';
export const L2_PRODUCT = 'cfa-l2-annual-v1';
export const L3_PRODUCT = 'cfa-l3-annual-v1';
export const L1_MONTHLY_PRODUCT = 'cfa-l1-monthly-v1';
export const L2_MONTHLY_PRODUCT = 'cfa-l2-monthly-v1';
export const L3_MONTHLY_PRODUCT = 'cfa-l3-monthly-v1';
export const PACKAGE_PRODUCT = 'cfa-package-lifetime-v1';

/** @deprecated Prefer getDynamicPricingL2().priceUsd — kept for older imports. */
export const L2_LIST_USD = L2_ANCHOR_PRICE_USD;
/** @deprecated Prefer live L2 ladder — first founding tier. */
export const L2_FOUNDING_USD = 149;
export const L3_LIST_USD = L2_ANCHOR_PRICE_USD;
export const L3_FOUNDING_USD = 149;
export { L1_MONTHLY_USD, L2_MONTHLY_USD, L3_MONTHLY_USD };

/** Lifetime package — access to this product’s L1–L3 package/updates, not CFA Institute. */
export const PACKAGE_PRICE_USD = 349;
export const PACKAGE_LIST_USD = 499;

export const LIFETIME_UNTIL = 'lifetime';

export const SKU_CATALOG = {
  [L1_ANNUAL_SKU]: {
    id: L1_ANNUAL_SKU,
    product: L1_PRODUCT,
    label: 'Level 1 lifetime',
    stripeName: 'CFA Mock Exam · Level 1 lifetime',
    levels: ['l1'],
    term: 'lifetime',
    listUsd: ANCHOR_PRICE_USD,
    foundingUsd: null,
    stripePriceEnv: 'STRIPE_PRICE_L1_ANNUAL',
    contentReady: true,
  },
  [L2_ANNUAL_SKU]: {
    id: L2_ANNUAL_SKU,
    product: L2_PRODUCT,
    label: 'Level 2 lifetime',
    stripeName: 'CFA Mock Exam · Level 2 lifetime',
    levels: ['l2'],
    term: 'lifetime',
    listUsd: L2_ANCHOR_PRICE_USD,
    foundingUsd: null,
    stripePriceEnv: 'STRIPE_PRICE_L2_ANNUAL',
    contentReady: true,
  },
  [L3_ANNUAL_SKU]: {
    id: L3_ANNUAL_SKU,
    product: L3_PRODUCT,
    label: 'Level 3 lifetime',
    stripeName: 'CFA Mock Exam · Level 3 lifetime',
    levels: ['l3'],
    term: 'lifetime',
    listUsd: L2_ANCHOR_PRICE_USD,
    foundingUsd: null,
    stripePriceEnv: 'STRIPE_PRICE_L3_ANNUAL',
    contentReady: true,
  },
  [L1_MONTHLY_SKU]: {
    id: L1_MONTHLY_SKU,
    product: L1_MONTHLY_PRODUCT,
    label: 'Level 1 monthly',
    stripeName: 'CFA Mock Exam · Level 1 monthly',
    levels: ['l1'],
    term: 'monthly',
    listUsd: L1_MONTHLY_USD,
    foundingUsd: null,
    stripePriceEnv: 'STRIPE_PRICE_L1_MONTHLY',
    contentReady: true,
  },
  [L2_MONTHLY_SKU]: {
    id: L2_MONTHLY_SKU,
    product: L2_MONTHLY_PRODUCT,
    label: 'Level 2 monthly',
    stripeName: 'CFA Mock Exam · Level 2 monthly',
    levels: ['l2'],
    term: 'monthly',
    listUsd: L2_MONTHLY_USD,
    foundingUsd: null,
    stripePriceEnv: 'STRIPE_PRICE_L2_MONTHLY',
    contentReady: true,
  },
  [L3_MONTHLY_SKU]: {
    id: L3_MONTHLY_SKU,
    product: L3_MONTHLY_PRODUCT,
    label: 'Level 3 monthly',
    stripeName: 'CFA Mock Exam · Level 3 monthly',
    levels: ['l3'],
    term: 'monthly',
    listUsd: L3_MONTHLY_USD,
    foundingUsd: null,
    stripePriceEnv: 'STRIPE_PRICE_L3_MONTHLY',
    contentReady: true,
  },
  [PACKAGE_LIFETIME_SKU]: {
    id: PACKAGE_LIFETIME_SKU,
    product: PACKAGE_PRODUCT,
    label: 'All-level lifetime package',
    stripeName: 'CFA Mock Exam · All-level lifetime package',
    levels: [...CFA_LEVELS],
    term: 'lifetime',
    listUsd: PACKAGE_LIST_USD,
    foundingUsd: PACKAGE_PRICE_USD,
    stripePriceEnv: 'STRIPE_PRICE_PACKAGE_LIFETIME',
    contentReady: true,
  },
};

const PRODUCT_TO_SKU = Object.values(SKU_CATALOG).reduce((acc, sku) => {
  acc[sku.product] = sku.id;
  return acc;
}, {});

export function resolveSkuId(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase();
  if (!raw || raw === 'l1' || raw === L1_ANNUAL_SKU || raw === L1_PRODUCT) return L1_ANNUAL_SKU;
  if (raw === 'l2' || raw === L2_ANNUAL_SKU || raw === L2_PRODUCT) return L2_ANNUAL_SKU;
  if (raw === 'l3' || raw === L3_ANNUAL_SKU || raw === L3_PRODUCT) return L3_ANNUAL_SKU;
  if (
    raw === 'package' ||
    raw === 'lifetime' ||
    raw === PACKAGE_LIFETIME_SKU ||
    raw === PACKAGE_PRODUCT
  ) {
    return PACKAGE_LIFETIME_SKU;
  }
  if (SKU_CATALOG[raw]) return raw;
  if (PRODUCT_TO_SKU[raw]) return PRODUCT_TO_SKU[raw];
  return L1_ANNUAL_SKU;
}

export function getSku(value) {
  return SKU_CATALOG[resolveSkuId(value)] || SKU_CATALOG[L1_ANNUAL_SKU];
}

export function isKnownProduct(product) {
  const raw = String(product || '').trim();
  if (!raw) return false;
  return Boolean(PRODUCT_TO_SKU[raw] || SKU_CATALOG[raw]);
}

export function isL1AnnualSku(value) {
  return resolveSkuId(value) === L1_ANNUAL_SKU;
}

export function isMonthlySku(value) {
  return getSku(value).term === 'monthly';
}

export function monthlySkuForLevel(level) {
  const normalized = String(level || '').trim().toLowerCase();
  if (normalized === 'l1') return L1_MONTHLY_SKU;
  if (normalized === 'l2') return L2_MONTHLY_SKU;
  if (normalized === 'l3') return L3_MONTHLY_SKU;
  return '';
}

export function isL2FamilySku(value) {
  const id = resolveSkuId(value);
  return id === L2_ANNUAL_SKU || id === L3_ANNUAL_SKU;
}

export function skuWritesLegacyPaidUntil(value) {
  const sku = getSku(value);
  return sku.levels.includes('l1') || sku.id === PACKAGE_LIFETIME_SKU;
}

/**
 * Quote a SKU. L1 and L2/L3 use live founding ladders (do not flatten to list).
 * Package price subtracts `creditUsd` (prior L1/L2/L3 annual spend → lifetime upgrade).
 */
export function quoteSku(skuValue, { now = new Date(), creditUsd = 0, tier = '' } = {}) {
  const quote = listQuoteSku(skuValue, { now, creditUsd });
  if (!tier) return quote;
  // Regional sale tier from /api/geo (server-resolved). Keep in sync with netlify/functions/_skus.js.
  const regionalUsd = tierPriceUsd(quote.sku, tier);
  // A sale price never exceeds the regular quote.
  if (regionalUsd == null || regionalUsd >= quote.priceUsd) return { ...quote, pricingTier: LIST_TIER };
  return { ...quote, priceUsd: regionalUsd, isFounding: false, pricingTier: tier };
}

function listQuoteSku(skuValue, { now = new Date(), creditUsd = 0 } = {}) {
  const sku = getSku(skuValue);

  if (sku.term === 'monthly') {
    return {
      sku: sku.id,
      product: sku.product,
      priceUsd: sku.listUsd,
      listUsd: sku.listUsd,
      isFounding: false,
      creditUsd: 0,
      term: sku.term,
      label: sku.label,
    };
  }

  if (sku.id === L1_ANNUAL_SKU) {
    const pricing = getDynamicPricing(now);
    return {
      sku: sku.id,
      product: sku.product,
      priceUsd: pricing.priceUsd,
      listUsd: sku.listUsd,
      isFounding: pricing.active && pricing.priceUsd < sku.listUsd,
      creditUsd: 0,
      term: sku.term,
      label: sku.label,
      nextPriceUsd: pricing.nextPriceUsd,
      msUntilPriceIncrease: pricing.msUntilPriceIncrease,
    };
  }

  if (sku.id === PACKAGE_LIFETIME_SKU) {
    const credit = Math.max(0, Number(creditUsd) || 0);
    const priceUsd = Math.max(0, PACKAGE_PRICE_USD - credit);
    return {
      sku: sku.id,
      product: sku.product,
      priceUsd,
      listUsd: PACKAGE_LIST_USD,
      isFounding: priceUsd < PACKAGE_LIST_USD,
      creditUsd: Math.min(credit, PACKAGE_PRICE_USD),
      term: sku.term,
      label: sku.label,
    };
  }

  if (sku.id === L2_ANNUAL_SKU || sku.id === L3_ANNUAL_SKU) {
    const pricing = getDynamicPricingL2(now);
    return {
      sku: sku.id,
      product: sku.product,
      priceUsd: pricing.priceUsd,
      listUsd: sku.listUsd,
      isFounding: pricing.active && pricing.priceUsd < sku.listUsd,
      creditUsd: 0,
      term: sku.term,
      label: sku.label,
      nextPriceUsd: pricing.nextPriceUsd,
      msUntilPriceIncrease: pricing.msUntilPriceIncrease,
    };
  }

  return {
    sku: sku.id,
    product: sku.product,
    priceUsd: sku.listUsd,
    listUsd: sku.listUsd,
    isFounding: false,
    creditUsd: 0,
    term: sku.term,
    label: sku.label,
  };
}

/** Price fragment only — never include the word "founding" (UI owns that label). */
export function skuPriceLabel(quote) {
  if (quote.term === 'lifetime') {
    if (quote.creditUsd > 0) {
      return `$${quote.priceUsd} after $${quote.creditUsd} purchase credit`;
    }
    return `$${quote.priceUsd} lifetime`;
  }
  if (quote.term === 'monthly') return `$${quote.priceUsd}/mo`;
  return `$${quote.priceUsd}/year`;
}

export const PACKAGE_LIFETIME_DISCLAIMER =
  'Lifetime means ongoing access to this product’s Level 1–3 package and updates. Amounts you already paid for Level 1–3 on this account are credited toward the package price. Not affiliated with CFA Institute.';
